/**
 * DialogBuscarHueco — Smart slot finder.
 *
 * Pide duración + rango de fechas y devuelve los primeros N slots libres
 * considerando el buffer del médico. Click en un slot → abre DialogNuevaCita pre-llenado.
 */
import React, { useState } from 'react';
import {
  Box, Typography, Button, IconButton, Dialog, Slide, TextField,
  FormControl, InputLabel, Select, MenuItem, Skeleton, Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Schedule as ScheduleIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

const API_URL = '/api';

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const inputStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2, background: '#f8fafc', fontSize: 13.5,
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: '#1e3a5f', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: 12.5, fontWeight: 600, color: '#475569' },
};

const DURACIONES = [15, 20, 30, 45, 60, 90, 120];

const DIAS_LARGO = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const MESES_CORTO = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function dateToInputs(d) {
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default function DialogBuscarHueco({ open, onClose, userId, onSlotElegido }) {
  const hoy = new Date();
  const en7 = new Date(hoy); en7.setDate(en7.getDate() + 7);
  const [duracion, setDuracion] = useState(30);
  const [desde, setDesde] = useState(dateToInputs(hoy));
  const [hasta, setHasta] = useState(dateToInputs(en7));
  const [slots, setSlots] = useState(null); // null = no buscado, [] = sin resultados
  const [meta, setMeta] = useState(null);
  const [buscando, setBuscando] = useState(false);

  const buscar = async () => {
    setBuscando(true);
    setSlots(null);
    try {
      const params = new URLSearchParams({
        duracion: String(duracion),
        desde, hasta,
        limite: '20',
      });
      const res = await fetch(`${API_URL}/emr/agenda/slots-libres?${params}`, {
        headers: { 'X-User-Id': userId },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setSlots(data.slots || []);
      setMeta({
        buffer_min: data.buffer_min,
        jornada: `${data.jornada_inicio} - ${data.jornada_fin}`,
      });
    } catch (e) {
      console.error(e);
      setSlots([]);
    } finally {
      setBuscando(false);
    }
  };

  const handleSeleccionar = (slot) => {
    onSlotElegido?.(new Date(slot.inicio));
    onClose();
  };

  // Agrupar slots por día
  const slotsPorDia = (slots || []).reduce((acc, s) => {
    const k = new Date(s.inicio).toDateString();
    if (!acc[k]) acc[k] = [];
    acc[k].push(s);
    return acc;
  }, {});

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={SlideUp}
      maxWidth="xs" fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3, background: 'rgba(255,255,255,0.98)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 24px 60px rgba(15,23,42,0.25)', overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.2,
        py: 1, pl: 2, pr: 1,
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)', color: '#fff',
      }}>
        <SearchIcon sx={{ fontSize: 18, color: '#fbbf24' }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: 0.2, lineHeight: 1.1 }}>
            Buscar hueco disponible
          </Typography>
          <Typography sx={{ fontSize: 10.5, opacity: 0.8 }}>
            Encuentra el primer slot libre considerando tu buffer
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}
          sx={{ color: 'rgba(255,255,255,0.85)', p: 0.4 }}>
          <CloseIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: '74vh', overflowY: 'auto' }}>
        {/* Filtros */}
        <Box sx={{ display: 'flex', gap: 1.2 }}>
          <FormControl size="small" sx={{ ...inputStyle, minWidth: 110 }}>
            <InputLabel shrink>Duración</InputLabel>
            <Select label="Duración" notched value={duracion} onChange={(e) => setDuracion(e.target.value)}>
              {DURACIONES.map(d => <MenuItem key={d} value={d}>{d} min</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            sx={{ ...inputStyle, flex: 1 }} size="small"
            type="date" label="Desde"
            value={desde} onChange={(e) => setDesde(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            sx={{ ...inputStyle, flex: 1 }} size="small"
            type="date" label="Hasta"
            value={hasta} onChange={(e) => setHasta(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Button
          variant="contained" startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
          onClick={buscar} disabled={buscando}
          sx={{
            textTransform: 'none', fontWeight: 700, fontSize: 13, borderRadius: 2, py: 1,
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)' },
          }}
        >
          {buscando ? 'Buscando...' : 'Buscar slots libres'}
        </Button>

        {/* Resultados */}
        {buscando && (
          <Box>
            {[1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={50} sx={{ mb: 0.8, borderRadius: 2 }} />)}
          </Box>
        )}

        {!buscando && slots !== null && slots.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center', borderRadius: 2, border: '1px dashed #cbd5e1', background: '#fafbfc' }}>
            <ScheduleIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
            <Typography sx={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
              Sin huecos disponibles en este rango
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
              Ajusta la duración o amplía el rango de fechas
            </Typography>
          </Box>
        )}

        {!buscando && slots && slots.length > 0 && (
          <Box>
            {meta && (
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small" label={`${slots.length} hueco${slots.length !== 1 ? 's' : ''}`}
                  sx={{ height: 20, fontSize: 10.5, fontWeight: 700, background: '#dcfce7', color: '#15803d' }}
                />
                <Chip
                  size="small" label={`Buffer ${meta.buffer_min} min`}
                  sx={{ height: 20, fontSize: 10.5, fontWeight: 700, background: '#eff6ff', color: '#1e3a5f' }}
                />
                <Chip
                  size="small" label={`Jornada ${meta.jornada}`}
                  sx={{ height: 20, fontSize: 10.5, fontWeight: 700, background: '#f1f5f9', color: '#475569' }}
                />
              </Box>
            )}
            {Object.entries(slotsPorDia).map(([dayKey, daySlots]) => {
              const fecha = new Date(dayKey);
              return (
                <Box key={dayKey} sx={{ mb: 1.5 }}>
                  <Typography sx={{
                    fontSize: 10.5, fontWeight: 800, color: '#1e3a5f',
                    textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.6,
                  }}>
                    {DIAS_LARGO[fecha.getDay()]} · {fecha.getDate()} {MESES_CORTO[fecha.getMonth()]}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 0.6 }}>
                    {daySlots.map((s, i) => {
                      const f = new Date(s.inicio);
                      return (
                        <Box
                          key={i}
                          onClick={() => handleSeleccionar(s)}
                          sx={{
                            p: 1, borderRadius: 1.5, cursor: 'pointer',
                            border: '1px solid #e2e8f0', background: '#fff',
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            transition: 'all 0.15s',
                            '&:hover': {
                              border: '1px solid #1e3a5f',
                              background: '#eff6ff',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(30,58,95,0.12)',
                            },
                          }}
                        >
                          <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#1e3a5f', lineHeight: 1 }}>
                            {f.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                          <Typography sx={{ fontSize: 9.5, color: '#64748b', mt: 0.3 }}>
                            {duracion} min
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
